import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { interval, fromEvent, Subscription, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isDevMode } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { AppUpdateOverlayContentComponent } from '@core/components/app-update-overlay-content/app-update-overlay-content.component';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateVersionService implements OnDestroy {
  private readonly RELOAD_PAGE_TIME = 15000;
  private readonly PROGRESS_INTERVAL_MS = 150;
  private visibilitySubscription: Subscription;
  private progressIntervalId: ReturnType<typeof setInterval> | null = null;

  /** Прогресс 0–100 для отображения в оверлее. */
  readonly updateProgressPercent$ = new BehaviorSubject<number>(0);

  constructor(
    private swUpdate: SwUpdate,
    private globalI18n: GlobalI18n,
    private modal: NzModalService
  ) {
    if (swUpdate.isEnabled) {
      // Проверка на наличие новой версии при старте и каждый час
      this.swUpdate.checkForUpdate();
      interval(60 * 60 * 1000).subscribe(() => this.swUpdate.checkForUpdate());

      // Проверка при возврате на вкладку (актуальная версия после долгой работы в другой вкладке)
      this.visibilitySubscription = fromEvent(document, 'visibilitychange')
        .pipe(filter(() => document.visibilityState === 'visible'))
        .subscribe(() => this.checkForUpdates());
    }

    this.swUpdate.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${evt.version.hash}`);
          break;
        case 'VERSION_READY':
          this.activateAndReload();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.visibilitySubscription?.unsubscribe();
  }

  public checkForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then(updateIsExist => {
        if (updateIsExist) {
          this.activateAndReload();
        }
      });
    }
  }

  /** Активирует новую версию и перезагружает страницу, блокируя интерфейс модалкой с прогрессом. */
  private async activateAndReload(): Promise<void> {
    this.updateProgressPercent$.next(0);

    this.modal.create({
      nzContent: AppUpdateOverlayContentComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzCentered: true,
      nzFooter: null,
      nzWidth: 560,
    });

    await this.swUpdate.activateUpdate();

    const start = Date.now();
    this.progressIntervalId = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.min(100, (elapsed / this.RELOAD_PAGE_TIME) * 100);
      this.updateProgressPercent$.next(percent);
    }, this.PROGRESS_INTERVAL_MS);

    setTimeout(() => {
      if (this.progressIntervalId != null) {
        clearInterval(this.progressIntervalId);
        this.progressIntervalId = null;
      }
      document.location.reload();
    }, this.RELOAD_PAGE_TIME);
  }

  getUpdateMessage(): string {
    return this.globalI18n.getMessage(Module.General, 'appUpdateTimer');
  }

  /**
   * Только для локальной проверки модалки обновления (режим разработки).
   * Показывает модалку и прогресс без перезагрузки, перезагрузка не выполняется для dev-mod.
   */
  showUpdateModalForTesting(): void {
    if (!isDevMode()) return;
    this.updateProgressPercent$.next(0);
    this.modal.create({
      nzContent: AppUpdateOverlayContentComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzCentered: true,
      nzFooter: null,
      nzWidth: 560,
    });
    const start = Date.now();
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.min(100, (elapsed / this.RELOAD_PAGE_TIME) * 100);
      this.updateProgressPercent$.next(percent);
      if (percent >= 100) clearInterval(intervalId);
    }, this.PROGRESS_INTERVAL_MS);
  }
}
